import { Text } from '@box/blueprint-web';
import * as React from 'react';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import useId from '../../../../../common/utils/useId';
import NumberInput from '../../../../../common/components/number-input';
import css from './number-field-submitter.module.scss';
import messages from './messages';

type Props = {
  className?: string;
  description?: React.ReactNode;
  error: MessageDescriptor;
  intl: IntlShape;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: React.ReactNode;
  onChange: (...args: unknown[]) => unknown;
  value: string;
  isForm?: boolean;
};

const NumberFieldSubmitter = React.memo<Props>((props: Props) => {
  const {
    intl,
    className,
    label,
    description,
    onChange,
    isRequired,
    isDisabled,
    error,
    value,
  } = props;

  const classes = classNames(className, css.numberField);
  const errorMessage = error ? intl.formatMessage(error) : undefined;
  const descriptionID = useId('description');
  const labelID = useId('label');
  const ariaAttrs = {
    'aria-describedby': description ? descriptionID : undefined,
    'aria-labelledby': labelID,
  } as const;
  const placeholder = intl.formatMessage(messages.numberPlaceholderText);

  return (
    <div className={classes}>
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
        className={css.label}
      >
        {label}
      </Text>
      <NumberInput
        {...ariaAttrs}
        disabled={isDisabled}
        error={errorMessage}
        onChange={onChange}
        placeholder={placeholder}
        required={isRequired}
        value={value}
      />
      {!!description && (
        <Text
          as="span"
          className={css.description}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}
    </div>
  );
});

export { NumberFieldSubmitter };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(NumberFieldSubmitter);
export default Wrapped as React.ComponentType<WrappedProps>;
